import { FormEvent, useEffect, useMemo, useState } from 'react';
import Card from '@/components/ui/Card';
import PublicPreviewButton from '@/components/sponsor/PublicPreviewButton';
import ProfileFeedEditor, { type SponsorFeedEntry } from '@/components/sponsor/profile/ProfileFeedEditor';
import { useAuth } from '@/lib/auth-context';
import { checkSponsorHandleAvailable, updateSponsorPublicProfile } from '@/lib/coreApi';

const HANDLE_REGEX = /^[a-z0-9-]{3,32}$/;

const ORG_KINDS = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'team', label: 'Team' },
];

const THEME_OPTIONS = [
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'bold', label: 'Bold' },
];

interface DashboardProfileData {
  handle: string | null;
  display_name: string | null;
  tagline: string | null;
  website_url: string | null;
  logo_url: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  is_public?: boolean | null;
  public_config?: any;
}

interface SponsorProfileDashboardProps {
  token: string | null;
  profile: DashboardProfileData | null;
  onSaved: () => void;
}

interface MomentDraft {
  id: string;
  title: string;
  kind: string;
  year: string;
  summary: string;
  classrooms: string;
  students: string;
  image_url: string;
  order: number;
  visible: boolean;
}

interface SignalDraft {
  id: string;
  label: string;
  value: string;
  unit: string;
  note: string;
}

function createMomentDraft(partial?: Partial<MomentDraft>, index = 0): MomentDraft {
  return {
    id: partial?.id || crypto.randomUUID?.() || `moment-${Date.now()}`,
    title: partial?.title || '',
    kind: partial?.kind || 'milestone',
    year: partial?.year || '',
    summary: partial?.summary || '',
    classrooms: partial?.classrooms || '',
    students: partial?.students || '',
    image_url: partial?.image_url || '',
    order: partial?.order ?? index + 1,
    visible: partial?.visible !== undefined ? partial.visible : true,
  };
}

function createSignalDraft(partial?: Partial<SignalDraft>, index = 0): SignalDraft {
  return {
    id: partial?.id || crypto.randomUUID?.() || `signal-${Date.now()}-${index}`,
    label: partial?.label || '',
    value: partial?.value || '',
    unit: partial?.unit || '',
    note: partial?.note || '',
  };
}

export default function SponsorProfileDashboardProfile({ token, profile, onSaved }: SponsorProfileDashboardProps) {
  const { profile: accountProfile } = useAuth();
  const fallbackEmail = (accountProfile?.email as string | undefined) || null;
  const [handle, setHandle] = useState(profile?.handle || '');
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [tagline, setTagline] = useState(profile?.tagline || '');
  const [heroSubheadline, setHeroSubheadline] = useState(profile?.public_config?.hero?.subheadline || '');
  const [websiteUrl, setWebsiteUrl] = useState(profile?.website_url || '');
  const [logoUrl, setLogoUrl] = useState(profile?.logo_url || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [isPublic, setIsPublic] = useState(Boolean(profile?.is_public ?? true));
  const [shortBio, setShortBio] = useState(profile?.public_config?.identity?.short_bio || '');
  const [location, setLocation] = useState(profile?.public_config?.identity?.location || '');
  const [orgKind, setOrgKind] = useState(profile?.public_config?.identity?.org_kind || 'business');
  const [focusTags, setFocusTags] = useState<string[]>(profile?.public_config?.focus_tags || []);
  const [tagInput, setTagInput] = useState('');
  const [theme, setTheme] = useState(profile?.public_config?.theme || 'warm');
  const [topMoments, setTopMoments] = useState<MomentDraft[]>(() =>
    Array.isArray(profile?.public_config?.top_moments)
      ? profile?.public_config?.top_moments.map((moment: any, index: number) =>
          createMomentDraft(
            {
              id: moment.id,
              title: moment.title,
              kind: moment.kind,
              year: moment.year ? String(moment.year) : '',
              summary: moment.summary,
              classrooms: moment?.impact?.classrooms ? String(moment.impact.classrooms) : '',
              students: moment?.impact?.students ? String(moment.impact.students) : '',
              image_url: moment?.media?.image_url,
              order: moment.order,
              visible: moment.visible !== false,
            },
            index,
          ),
        )
      : [],
  );
  const [signals, setSignals] = useState<SignalDraft[]>(() =>
    Array.isArray(profile?.public_config?.signal_highlights)
      ? profile?.public_config?.signal_highlights.map((signal: any, index: number) =>
          createSignalDraft(
            {
              id: signal.id,
              label: signal.label,
              value: signal.value ? String(signal.value) : '',
              unit: signal.unit,
              note: signal.note,
            },
            index,
          ),
        )
      : [],
  );
  const [modules, setModules] = useState(() => ({
    show_stats: profile?.public_config?.modules?.show_stats !== false,
    show_quote: profile?.public_config?.modules?.show_quote !== false,
    show_activity: profile?.public_config?.modules?.show_activity !== false,
    show_signal_highlights: profile?.public_config?.modules?.show_signal_highlights !== false,
    show_moments: profile?.public_config?.modules?.show_moments !== false,
    show_feed: profile?.public_config?.modules?.show_feed !== false,
  }));
  const [feedEntries, setFeedEntries] = useState<SponsorFeedEntry[]>(() =>
    Array.isArray(profile?.public_config?.feed_entries)
      ? profile.public_config.feed_entries.map((entry: any) => ({
          id: entry.id || crypto.randomUUID?.() || `feed-${Date.now()}`,
          type: entry.type || 'story',
          title: entry.title || '',
          summary: entry.summary || null,
          media_url: entry.media_url || null,
          cta_text: entry.cta_text || null,
          cta_url: entry.cta_url || null,
          tag: entry.tag || null,
          published_at: entry.published_at || null,
        }))
      : [],
  );
  const [quoteText, setQuoteText] = useState(profile?.public_config?.quote?.text || '');
  const [quotePerson, setQuotePerson] = useState(profile?.public_config?.quote?.person || '');
  const [quoteTitle, setQuoteTitle] = useState(profile?.public_config?.quote?.title || '');
  const [message, setMessage] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [handleState, setHandleState] = useState<'idle' | 'checking' | 'ok' | 'taken'>('idle');
  const [handleHint, setHandleHint] = useState('');

  useEffect(() => {
    setHandle(profile?.handle || '');
    setDisplayName(profile?.display_name || '');
    setTagline(profile?.tagline || '');
    setHeroSubheadline(profile?.public_config?.hero?.subheadline || '');
    setWebsiteUrl(profile?.website_url || '');
    setLogoUrl(profile?.logo_url || '');
    setAvatarUrl(profile?.avatar_url || '');
    setBio(profile?.bio || '');
    setIsPublic(Boolean(profile?.is_public ?? true));
    setShortBio(profile?.public_config?.identity?.short_bio || '');
    setLocation(profile?.public_config?.identity?.location || '');
    setOrgKind(profile?.public_config?.identity?.org_kind || 'business');
    setFocusTags(profile?.public_config?.focus_tags || []);
    setTheme(profile?.public_config?.theme || 'warm');
    setTopMoments(
      Array.isArray(profile?.public_config?.top_moments)
        ? profile.public_config.top_moments.map((moment: any, index: number) =>
            createMomentDraft(
              {
                id: moment.id,
                title: moment.title,
                kind: moment.kind,
                year: moment.year ? String(moment.year) : '',
                summary: moment.summary,
                classrooms: moment?.impact?.classrooms ? String(moment.impact.classrooms) : '',
                students: moment?.impact?.students ? String(moment.impact.students) : '',
                image_url: moment?.media?.image_url,
                order: moment.order,
                visible: moment.visible !== false,
              },
              index,
            ),
          )
        : [],
    );
    setSignals(
      Array.isArray(profile?.public_config?.signal_highlights)
        ? profile.public_config.signal_highlights.map((signal: any, index: number) =>
            createSignalDraft(
              {
                id: signal.id,
                label: signal.label,
                value: signal.value ? String(signal.value) : '',
                unit: signal.unit,
                note: signal.note,
              },
              index,
            ),
          )
        : [],
    );
    setModules({
      show_stats: profile?.public_config?.modules?.show_stats !== false,
      show_quote: profile?.public_config?.modules?.show_quote !== false,
      show_activity: profile?.public_config?.modules?.show_activity !== false,
      show_signal_highlights: profile?.public_config?.modules?.show_signal_highlights !== false,
      show_moments: profile?.public_config?.modules?.show_moments !== false,
      show_feed: profile?.public_config?.modules?.show_feed !== false,
    });
    setFeedEntries(
      Array.isArray(profile?.public_config?.feed_entries)
        ? profile.public_config.feed_entries.map((entry: any) => ({
            id: entry.id || crypto.randomUUID?.() || `feed-${Date.now()}`,
            type: entry.type || 'story',
            title: entry.title || '',
            summary: entry.summary || null,
            media_url: entry.media_url || null,
            cta_text: entry.cta_text || null,
            cta_url: entry.cta_url || null,
            tag: entry.tag || null,
            published_at: entry.published_at || null,
          }))
        : [],
    );
    setQuoteText(profile?.public_config?.quote?.text || '');
    setQuotePerson(profile?.public_config?.quote?.person || '');
    setQuoteTitle(profile?.public_config?.quote?.title || '');
    setMessage('');
    setHandleState('idle');
    setHandleHint('');
  }, [profile?.handle]);

  useEffect(() => {
    const normalized = handle.trim().toLowerCase();
    if (!normalized || normalized === (profile?.handle || '')) {
      setHandleState('idle');
      setHandleHint('');
      return;
    }

    let cancelled = false;
    setHandleState('checking');
    setHandleHint('Checking availability…');

    const timeout = setTimeout(async () => {
      try {
        const result = await checkSponsorHandleAvailable(normalized);
        if (cancelled) return;
        if (result?.available) {
          setHandleState('ok');
          setHandleHint('Handle available.');
        } else {
          setHandleState('taken');
          setHandleHint('Handle not available.');
        }
      } catch (_err) {
        if (cancelled) return;
        setHandleState('idle');
        setHandleHint('Unable to check handle right now.');
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [handle, profile?.handle]);

  const normalizedTags = useMemo(() => focusTags.filter(Boolean), [focusTags]);

  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;
    if (focusTags.includes(value)) {
      setTagInput('');
      return;
    }
    setFocusTags([...focusTags, value]);
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFocusTags((prev) => prev.filter((t) => t !== tag));
  };

  const moveMoment = (index: number, direction: -1 | 1) => {
    setTopMoments((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((moment, idx) => ({ ...moment, order: idx + 1 }));
    });
  };

  const addMoment = () => {
    setTopMoments((prev) => [...prev, createMomentDraft({}, prev.length)]);
  };

  const removeMoment = (id: string) => {
    setTopMoments((prev) => prev.filter((moment) => moment.id !== id));
  };

  const addSignal = () => {
    setSignals((prev) => [...prev, createSignalDraft({}, prev.length)]);
  };

  const removeSignal = (id: string) => {
    setSignals((prev) => prev.filter((signal) => signal.id !== id));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setMessage('Sign in to update your sponsor profile.');
      return;
    }

    const normalizedHandle = handle.trim().toLowerCase();
    if (normalizedHandle && !HANDLE_REGEX.test(normalizedHandle)) {
      setMessage('Handle must be 3-32 characters using a-z, 0-9, or hyphen.');
      return;
    }

    setSaving(true);
    setMessage('');

    const payload = {
      handle: normalizedHandle || null,
      display_name: displayName.trim() || null,
      tagline: tagline.trim() || null,
      website_url: websiteUrl.trim() || null,
      logo_url: logoUrl.trim() || null,
      avatar_url: avatarUrl.trim() || null,
      bio: bio.trim() || null,
      is_public: isPublic,
      public_config: {
        theme,
        hero: {
          headline: tagline.trim() || null,
          subheadline: heroSubheadline.trim() || null,
        },
        identity: {
          org_kind: orgKind,
          location: location.trim() || null,
          industries: [],
          short_bio: shortBio.trim() || null,
        },
        focus_tags: normalizedTags,
        top_moments: topMoments.map((moment, index) => ({
          id: moment.id,
          title: moment.title,
          kind: moment.kind,
          year: moment.year ? Number(moment.year) : null,
          summary: moment.summary,
          impact: {
            classrooms: moment.classrooms ? Number(moment.classrooms) : null,
            students: moment.students ? Number(moment.students) : null,
          },
          media: {
            image_url: moment.image_url || null,
          },
          order: index + 1,
          visible: moment.visible,
        })),
        signal_highlights: signals
          .filter((signal) => signal.label)
          .map((signal) => ({
            id: signal.id,
            label: signal.label,
            value: signal.value ? Number(signal.value) : null,
            unit: signal.unit || null,
            note: signal.note || null,
          })),
        quote: {
          text: quoteText.trim() || null,
          person: quotePerson.trim() || null,
          title: quoteTitle.trim() || null,
        },
        modules,
        feed_entries: feedEntries
          .filter((entry) => entry.title?.trim())
          .map((entry) => ({
            id: entry.id,
            type: entry.type,
            title: entry.title,
            summary: entry.summary || null,
            media_url: entry.media_url || null,
            cta_text: entry.cta_text || null,
            cta_url: entry.cta_url || null,
            tag: entry.tag || null,
            published_at: entry.published_at || null,
          })),
      },
    };

    try {
      await updateSponsorPublicProfile(payload);

      setMessage('Profile updated!');
      setHandleState('idle');
      setHandleHint('');
      onSaved();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Unable to save profile right now');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="space-y-4">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-cocoa">Public profile basics</h2>
            <p className="text-sm text-cocoa/70">Update your handle, headline, and basic identity details.</p>
          </div>
          <PublicPreviewButton handle={handle} displayName={displayName} email={fallbackEmail} />
        </header>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-cocoa">
              Handle
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={handle}
                onChange={(event) => setHandle(event.target.value)}
                placeholder="acme-co"
              />
              {handleHint && (
                <p className={`mt-1 text-xs ${handleState === 'taken' ? 'text-brick' : 'text-cocoa/70'}`}>{handleHint}</p>
              )}
            </label>
            <label className="text-sm font-medium text-cocoa">
              Display name
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Acme Co."
              />
            </label>
            <label className="md:col-span-2 text-sm font-medium text-cocoa">
              Tagline / headline
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={tagline}
                onChange={(event) => setTagline(event.target.value)}
                placeholder="Backing classrooms that spark imagination."
              />
            </label>
            <label className="md:col-span-2 text-sm font-medium text-cocoa">
              Hero subheadline
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={heroSubheadline}
                onChange={(event) => setHeroSubheadline(event.target.value)}
                placeholder="Share a one-sentence story about your partnership."
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Website URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={websiteUrl}
                onChange={(event) => setWebsiteUrl(event.target.value)}
                placeholder="https://example.com"
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Logo URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={logoUrl}
                onChange={(event) => setLogoUrl(event.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Avatar URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
                value={avatarUrl}
                onChange={(event) => setAvatarUrl(event.target.value)}
                placeholder="https://example.com/avatar.png"
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Short bio
              <textarea
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                rows={3}
                value={bio}
                onChange={(event) => setBio(event.target.value)}
                placeholder="Tell the Tower community about your sponsorship focus."
              />
            </label>
            <div className="flex flex-col gap-2 text-sm text-cocoa md:col-span-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} />
                Show my profile publicly
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="rounded-xl bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-cocoa/60"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
            {message && <span className="text-sm text-cocoa">{message}</span>}
          </div>
        </form>
      </Card>

      <Card className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold text-cocoa">Story & focus</h2>
          <p className="text-sm text-cocoa/70">Add your organization type, location, and tags.</p>
        </header>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-cocoa">
            Org type
            <select
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
              value={orgKind}
              onChange={(event) => setOrgKind(event.target.value)}
            >
              {ORG_KINDS.map((kind) => (
                <option key={kind.value} value={kind.value}>
                  {kind.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-cocoa">
            Location
            <input
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Detroit, MI"
            />
          </label>
          <label className="md:col-span-2 text-sm font-medium text-cocoa">
            Short bio for the public page
            <textarea
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
              rows={3}
              value={shortBio}
              onChange={(event) => setShortBio(event.target.value)}
              placeholder="We support hands-on learning and creative classrooms."
            />
          </label>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-cocoa">Focus tags</p>
          <div className="flex flex-wrap gap-2">
            {normalizedTags.map((tag) => (
              <span key={tag} className="flex items-center gap-2 rounded-full bg-sand px-3 py-1 text-xs font-semibold text-cocoa">
                {tag}
                <button type="button" className="text-cocoa/60" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none sm:w-auto sm:flex-1"
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTag();
                }
              }}
              placeholder="Add a focus tag"
            />
            <button
              type="button"
              onClick={addTag}
              className="rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa shadow-sm"
            >
              Add tag
            </button>
          </div>
        </div>
      </Card>

      <Card className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold text-cocoa">Top moments</h2>
          <p className="text-sm text-cocoa/70">Select the milestones, campaigns, and initiatives to feature publicly.</p>
        </header>
        <div className="flex flex-col gap-4">
          {topMoments.map((moment, index) => (
            <div key={moment.id} className="rounded-2xl border border-sand px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-cocoa">Moment {index + 1}</p>
                <div className="flex items-center gap-2 text-xs text-cocoa">
                  <button type="button" className="rounded-lg border border-sand px-2 py-1" onClick={() => moveMoment(index, -1)}>
                    ↑
                  </button>
                  <button type="button" className="rounded-lg border border-sand px-2 py-1" onClick={() => moveMoment(index, 1)}>
                    ↓
                  </button>
                  <button type="button" className="text-brick" onClick={() => removeMoment(moment.id)}>
                    Remove
                  </button>
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm font-medium text-cocoa">
                  Title
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={moment.title}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, title: event.target.value };
                        return next;
                      })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Type
                  <select
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={moment.kind}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, kind: event.target.value };
                        return next;
                      })
                    }
                  >
                    <option value="milestone">Milestone</option>
                    <option value="campaign">Campaign</option>
                    <option value="initiative">Initiative</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Year
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={moment.year}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, year: event.target.value };
                        return next;
                      })
                    }
                    placeholder="2026"
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Image URL
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={moment.image_url}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, image_url: event.target.value };
                        return next;
                      })
                    }
                    placeholder="https://example.com/moment.jpg"
                  />
                </label>
                <label className="md:col-span-2 text-sm font-medium text-cocoa">
                  Summary
                  <textarea
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    rows={3}
                    value={moment.summary}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, summary: event.target.value };
                        return next;
                      })
                    }
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2 md:col-span-2">
                  <label className="text-sm font-medium text-cocoa">
                    Classrooms impacted
                    <input
                      className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                      value={moment.classrooms}
                      onChange={(event) =>
                        setTopMoments((prev) => {
                          const next = [...prev];
                          next[index] = { ...moment, classrooms: event.target.value };
                          return next;
                        })
                      }
                      placeholder="20"
                    />
                  </label>
                  <label className="text-sm font-medium text-cocoa">
                    Students reached
                    <input
                      className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                      value={moment.students}
                      onChange={(event) =>
                        setTopMoments((prev) => {
                          const next = [...prev];
                          next[index] = { ...moment, students: event.target.value };
                          return next;
                        })
                      }
                      placeholder="480"
                    />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm text-cocoa">
                  <input
                    type="checkbox"
                    checked={moment.visible}
                    onChange={(event) =>
                      setTopMoments((prev) => {
                        const next = [...prev];
                        next[index] = { ...moment, visible: event.target.checked };
                        return next;
                      })
                    }
                  />
                  Show on public page
                </label>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addMoment}
            className="w-full rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa shadow-sm"
          >
            Add moment
          </button>
        </div>
      </Card>

      <Card className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold text-cocoa">Signals & modules</h2>
          <p className="text-sm text-cocoa/70">Tune the metrics strip and choose which modules show publicly.</p>
        </header>
        <div className="flex flex-col gap-4">
          {signals.map((signal, index) => (
            <div key={signal.id} className="rounded-2xl border border-sand px-4 py-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-cocoa">Signal {index + 1}</p>
                <button type="button" className="text-xs text-brick" onClick={() => removeSignal(signal.id)}>
                  Remove
                </button>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm font-medium text-cocoa">
                  Label
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={signal.label}
                    onChange={(event) =>
                      setSignals((prev) => {
                        const next = [...prev];
                        next[index] = { ...signal, label: event.target.value };
                        return next;
                      })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Value
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={signal.value}
                    onChange={(event) =>
                      setSignals((prev) => {
                        const next = [...prev];
                        next[index] = { ...signal, value: event.target.value };
                        return next;
                      })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Unit
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={signal.unit}
                    onChange={(event) =>
                      setSignals((prev) => {
                        const next = [...prev];
                        next[index] = { ...signal, unit: event.target.value };
                        return next;
                      })
                    }
                    placeholder="students"
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Note
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={signal.note}
                    onChange={(event) =>
                      setSignals((prev) => {
                        const next = [...prev];
                        next[index] = { ...signal, note: event.target.value };
                        return next;
                      })
                    }
                    placeholder="All-time on Tower"
                  />
                </label>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addSignal}
            className="w-full rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa shadow-sm"
          >
            Add signal highlight
          </button>
        </div>
        <div className="grid gap-2 text-sm text-cocoa md:grid-cols-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_stats}
              onChange={(event) => setModules((prev) => ({ ...prev, show_stats: event.target.checked }))}
            />
            Show stats block
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_quote}
              onChange={(event) => setModules((prev) => ({ ...prev, show_quote: event.target.checked }))}
            />
            Show quote
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_activity}
              onChange={(event) => setModules((prev) => ({ ...prev, show_activity: event.target.checked }))}
            />
            Show activity feed
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_signal_highlights}
              onChange={(event) => setModules((prev) => ({ ...prev, show_signal_highlights: event.target.checked }))}
            />
            Show signal highlights
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_moments}
              onChange={(event) => setModules((prev) => ({ ...prev, show_moments: event.target.checked }))}
            />
            Show top moments
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.show_feed}
              onChange={(event) => setModules((prev) => ({ ...prev, show_feed: event.target.checked }))}
            />
            Show profile feed
          </label>
        </div>
      </Card>

      <Card className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold text-cocoa">Theme & quote</h2>
          <p className="text-sm text-cocoa/70">Choose a palette and highlight a signature quote.</p>
        </header>
        <div className="grid gap-4 sm:grid-cols-2">
          {THEME_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                theme === option.value ? 'border-cocoa bg-sand' : 'border-sand hover:border-cocoa/60'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-sm font-medium text-cocoa md:col-span-2">
            Quote text
            <textarea
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
              rows={3}
              value={quoteText}
              onChange={(event) => setQuoteText(event.target.value)}
              placeholder="We believe every kid deserves a lab."
            />
          </label>
          <div className="space-y-3">
            <label className="text-sm font-medium text-cocoa">
              Quote person
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={quotePerson}
                onChange={(event) => setQuotePerson(event.target.value)}
                placeholder="Jamie Rivera"
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Title
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={quoteTitle}
                onChange={(event) => setQuoteTitle(event.target.value)}
                placeholder="VP, Community Impact"
              />
            </label>
          </div>
        </div>
      </Card>

      <Card className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold text-cocoa">Profile feed</h2>
          <p className="text-sm text-cocoa/70">
            Share updates, milestones, and digital goods with the community. These appear on your public profile like a LinkedIn feed.
          </p>
        </header>
        <ProfileFeedEditor entries={feedEntries} onChange={setFeedEntries} />
      </Card>
    </div>
  );
}
